import React, {useState, useEffect} from "react";
import {
  Grid, Paper, Box, TextField, Typography, Switch,
  Tabs,
  Tab,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {Controller, useForm} from "react-hook-form";
import {SavingButton} from "@/components/data-grid/CustomButton";
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";
import {useTranslation} from "react-i18next";
import {object, TypeOf, z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import TimecardService from "@/services/att/services/TimecardService";
import SnackbarUtil from "@/utils/SnackbarUtil";
import moment from "moment";
import GenericService from "@/services/GenericService";
import {ATTEndpoints} from "@/services/att/endpoints";


type ManualShareProps = {
  report_type: string;
  date_format: string;
  payment_status?: boolean;
}

const NewManualShare = (props: ManualShareProps) => {
  const {t} = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0)
  const exportSchema = object({
    share: z.string().min(1, t("common.This field is required!")),
    start_date:
      z.date(
        {
          errorMap: (issue, {defaultError}) =>
            ({message: issue.code === "invalid_date" ? t("common.Invalid date format.") : issue.code === "invalid_type" ? t("common.This field is required!") : defaultError})
        }),
    end_date:
      z.date(
        {
          errorMap: (issue, {defaultError}) =>
            ({message: issue.code === "invalid_date" ? t("common.Invalid time format.") : issue.code === "invalid_type" ? t("common.This field is required!") : defaultError})
        }),
    with_tz: z.boolean()
  })
  type ExportType = TypeOf<typeof exportSchema>;

  const manualShareSettingForm = useForm<ExportType>({
    resolver: zodResolver(exportSchema),
    defaultValues: {
      start_date: today,
      end_date: today,
      with_tz: true
    }
  });

  const timecard_export_meta = {
    fields: {
      employee_code: {
        alias: t("common.Person ID")
      },
      first_name: {
        alias: t("common.First Name")
      },
      last_name: {
        alias: t("common.Last Name")
      },
      att_date: {
        alias: t("common.Date")
      },
      timesheet_name: {
        alias: t("common.Timesheet")
      },
      clock_in: {
        alias: t("common.Clock In")
      },
      clock_out: {
        alias: t("common.Clock Out")
      },
      period_hrs: {
        alias: t("common.Clock Time(h)")
      },
      paid_break: {
        alias: t("common.Total Break Time(h)")
      },
      paid_work_deduct_break: {
        alias: t("common.Total Work Time(h)")
      },
      overtime_hrs: {
        alias: t("common.Total Overtime Time(h)"),
      },
      paid_work: {
        alias: t("common.Total Time(h)")
      },
      pairing: {
        alias: t("common.Statistic Rule Mode")
      },
      abnormal_situation: {
        alias: t("common.Abnormal Situation")
      }
    },
    title: t("common.Timecard Report"),
    file_type: "csv",
    share: []
  }

  const attendance_export_meta = {
    fields: {
      employee_code: {
        alias: t("common.Person ID")
      },
      employee_name: {
        alias: t("common.Person Name")
      },
      timesheet_names: {
        alias: t("common.Timesheet")
      },
      att_dates: {
        alias: t("common.Date")
      },
      total_late_in: {
        alias: t("common.total_late_in")
      },
      total_early_out: {
        alias: t("common.total_early_out")
      },
      total_break: {
        alias: t("common.Total_break")
      },
      total_paid_work_deduct_break: {
        alias: t("common.Total_paid_work")
      },
      total_overtime_hrs: {
        alias: t("common.Total_overtime")
      },
      total_paid_work: {
        alias: t("common.Total_work")
      }
    },
    title: t("common.Attendance Report"),
    file_type: "csv",
    share: []
  }


  const report_export_meta = props.report_type == "timecard" ? timecard_export_meta : attendance_export_meta


  const onSubmit = async (formData: any) => {
    const {share} = formData;
    let isInvalidEmail = false;
    const share_email = share
      .trim()
      .replace(/[\r\n\s]/g, "")
      .split(";")
      .filter((item: string) => {
        const email_pattern = /^\w[-\w.+]*@([A-Za-z0-9][-A-Za-z0-9]+\.)+[A-Za-z]{2,14}$/;
        if (item !== "" && email_pattern.test(item)) {
          return true;
        }
        isInvalidEmail = true;
        return false;
      });

    if (isInvalidEmail) {
      manualShareSettingForm.setError("share", {type: "custom", message: "invalid email format"}, {shouldFocus: true});
      return;
    }

    setLoading(true);
    // 将日期格式转换为 moment.js 兼容的格式
    const convertToMomentFormat = (format: string) => {
      return format
        .replace(/yyyy/g, 'YYYY')
        .replace(/dd/g, 'DD');
    };

    // 通用日期解析函数
    const parseDate = (dateStr: string) => {
      // 1. 先尝试用 props.date_format 转换后的格式解析
      const convertedFormat = convertToMomentFormat(props.date_format);
      let m = moment(dateStr, convertedFormat, true);
      if (m.isValid()) {
        return m;
      }

      // 2. 尝试其他可能的格式（都转换为 moment 格式）
      const possibleFormats = [
        'MM/DD/YYYY',
        'DD/MM/YYYY',
        'YYYY-MM-DD',
      ];

      for (const format of possibleFormats) {
        m = moment(dateStr, format, true);
        if (m.isValid()) {
          return m;
        }
      }

      // 3. 最后尝试宽松模式
      m = moment(dateStr);
      if (m.isValid()) {
        return m;
      }

      return moment.invalid();
    };

    let params = '';

    if (selectedTab === 0) {
      // 拆分日期范围字符串
      const startStr = selectedPeriod.slice(0, 10);
      const endStr = selectedPeriod.slice(11, 21);

      // 解析日期
      const startMoment = parseDate(startStr);
      const endMoment = parseDate(endStr);

      // 验证解析是否成功
      if (!startMoment.isValid() || !endMoment.isValid()) {
        manualShareSettingForm.setError("share", {
          type: "custom",
          message: "Invalid date format in selected period"
        });
        setLoading(false);
        return;
      }

      // 统一输出为 YYYY-MM-DD 格式
      params = `date_range=${startMoment.format('YYYY-MM-DD')}&date_range=${endMoment.format('YYYY-MM-DD')}`;

    } else {
      // 自定义日期范围
      const startDate = manualShareSettingForm.getValues("start_date");
      const endDate = manualShareSettingForm.getValues("end_date");

      if (!startDate || !endDate) {
        manualShareSettingForm.setError("start_date", {
          type: "custom",
          message: "Please select start date"
        });
        setLoading(false);
        return;
      }

      const startMoment = moment(startDate);
      const endMoment = moment(endDate);

      if (!startMoment.isValid() || !endMoment.isValid()) {
        manualShareSettingForm.setError("start_date", {
          type: "custom",
          message: "Invalid date"
        });
        setLoading(false);
        return;
      }

      params = `date_range=${startMoment.format('YYYY-MM-DD')}&date_range=${endMoment.format('YYYY-MM-DD')}`;
    }

    const data = {...report_export_meta, share: share_email};
    const res = await TimecardService.advanced_export_data(params, data, props.report_type);

    SnackbarUtil.success(t("common.success"));
    setLoading(false);
  };
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState('05/03/2026-05/09/2026');

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  const handlePeriodChange = (event: any) => {
    setSelectedPeriod(event.target.value);
  };
  const [payPeriods, setPayPeriods] = useState<any>([]);
  useEffect(() => {
    const fetchSettings = async () => {
      GenericService.list(ATTEndpoints.ATT_GET_DATE_RANGE_URI, {current: 0, pageSize: 100}).then((res) => {
        if (res.data && res.status === 200) {
          const formattedPeriods = res.data.date_range.map((period: string[]) => {
            return `${period[0]}-${period[1]}`;
          });
          setPayPeriods(formattedPeriods);
          setSelectedPeriod(formattedPeriods[0])
        }
      })
    };

    fetchSettings();
  }, [])

  return (
    <>
      <Grid component={'form'} sx={{padding: '24px'}}>
        <Grid sx={{marginBottom: '20px'}}>
          <Typography sx={{color: "#999999"}}>{t("export.Select date period")}</Typography>
        </Grid>
        <Paper elevation={0} sx={{lineHeight: '40px'}}>
          {props.payment_status && <Box sx={{width: '100%', maxWidth: 400}}>
            {/* Tab Headers */}
            <Paper sx={{borderRadius: 1, mb: 3}}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  '& .MuiTabs-indicator': {
                    display: 'none', // 隐藏默认下划线
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: "normal",
                    padding: '16px',
                    height: '42px',
                    fontFamily: "Alibaba PuHuiTi 2.0",
                    backgroundColor: '#ffffff',
                    color: "#3D3D3D",
                    '&:first-of-type': {
                      borderRadius: '4px 0 0 4px',
                    },
                    '&:last-of-type': {
                      borderRadius: '0 4px 4px 0',
                    },
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#2BA9DE !important',
                    color: 'white !important',
                  },
                }}
              >
                <Tab label={t("common.Choose Pay Period")}/>
                <Tab label={t("common.Custom Date Range")}/>
              </Tabs>
            </Paper>

            {/* Pay Period Radio Buttons */}
            {selectedTab === 0 ? <FormControl component="fieldset" sx={{width: '100%', mb: 4}}>
              <RadioGroup
                value={selectedPeriod}
                onChange={handlePeriodChange}
                sx={{gap: 2}}
              >
                {payPeriods.map((period: any) => (
                  <FormControlLabel
                    key={period}
                    value={period}
                    control={<Radio/>}
                    label={
                      <Typography sx={{ml: 1, fontSize: "14px", color: "#3D3D3D", fontFamily: "Alibaba PuHuiTi 2.0"}}>
                        {period}
                      </Typography>
                    }
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      width: '100%',
                      height: '42px',
                      margin: 0,
                      '& .MuiRadio-root': {
                        color: 'white',
                        '&.Mui-checked': {
                          color: '#2BA9DE',
                        },
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl> : <>            <Grid container>
              <Grid lg={8} md={8} sm={8} item>
                <Box sx={{marginBottom: '8px'}}>
                  <Typography sx={{
                    fontSize: "14px",
                    color: "#3D3D3D",
                    fontFamily: "Alibaba PuHuiTi 2.0"
                  }}>{t("common.From")}</Typography>
                </Box>
                <Controller
                  name="start_date"
                  control={manualShareSettingForm.control}
                  render={({field, fieldState}) =>
                    <DatePicker
                      disableFuture
                      value={field.value || null}
                      inputFormat={props.date_format}
                      onChange={(newValue: any) => {
                        if (newValue > manualShareSettingForm.getValues("end_date")) {
                          SnackbarUtil.error(t("export.Start date cannot greater than end date"), {
                            anchorOrigin: {
                              vertical: 'top',
                              horizontal: 'center'
                            }
                          })
                          return;
                        }
                        field.onChange(newValue)
                      }}
                      renderInput={(params) =>
                        <TextField
                          sx={{width: '420px'}}
                          {...params}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || ""}
                        />}
                    />
                  }
                />
              </Grid>
            </Grid>
              <Grid container sx={{marginTop: '16px'}}>
                <Grid lg={8} md={8} sm={8} item>
                  <Box sx={{marginBottom: '8px'}}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#3D3D3D",
                        fontFamily: "Alibaba PuHuiTi 2.0"
                      }}>{t("common.To")}</Typography>
                  </Box>
                  <Controller
                    name="end_date"
                    control={manualShareSettingForm.control}
                    render={({field, fieldState}) =>
                      <DatePicker
                        disableFuture
                        value={field.value || null}
                        inputFormat={props.date_format}
                        onChange={(newValue: any) => {
                          if (newValue < manualShareSettingForm.getValues("start_date")) {
                            SnackbarUtil.error(t("export.End date cannot less than start date"), {
                              anchorOrigin: {
                                vertical: 'top',
                                horizontal: 'center'
                              }
                            })
                            return;
                          }
                          field.onChange(newValue)
                        }}
                        renderInput={(params) =>
                          <TextField
                            sx={{width: '420px'}}
                            {...params}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />}
                      />
                    }
                  />
                </Grid>
              </Grid></>}
          </Box>}
          <Grid sx={{marginTop: '8px', width: '100%'}}>
            <Box padding={"8px 0"}>
              <Typography sx={{color: "#999999"}}>{t("export.Email List")}</Typography>
            </Box>
            <ControlledTextFiled
              hookForm={manualShareSettingForm}
              sx={{width: '420px'}}
              name="share"
              multiline
              minRows={5}
              maxRows={5}
              type="text"
            />
            <Grid sx={{display: 'flex', justifyContent: 'start', marginTop: '16px'}}>
              <SavingButton
                loading={loading}
                disableElevation
                type="submit"
                children={t("common.confirm")}
                onClick={manualShareSettingForm.handleSubmit(onSubmit)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </>
  );
}

export default NewManualShare;

